
  import Vue, { computed, defineComponent, ref, watch, getCurrentInstance } from 'vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import { Icon } from '@/ui/components/icon/Icon';
  import { extensionsWithNoMimeType, getMimeTypes, isFileSizeValid } from '@/services/assets-api/assetsValidation';
  import {
    AssetsDocumentActions,
    AssetsDocumentStub,
    getDocumentStub,
  } from '@/services/assets-api/AssetsDocumentService';
  import { setFileToAdd, setFileToDelete } from '@/services/assets-api/AssetsFileStateService';
  import AttachedFile from '@/_legacy/components/ConductInspection/files/AttachedFile.vue';
  import { formatBytes } from '@/services/file/fileProcessor';
  import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
  import { toasted } from '@/ui/helpers';
  import { t } from '@/ui/i18n/i18n';
  import ActionButton from '@/_legacy/components/ConductInspection/buttons/ActionButton.vue';
  import PhotoButton from '@/_legacy/components/ConductInspection/buttons/PhotoButton.vue';
  import RaiseIssueModal from '@/_legacy/views/ConductInspection/Modals/RaiseIssueModal.vue';
  import CommentButton from '@/_legacy/components/ConductInspection/buttons/CommentButton.vue';
  import { AssetsPhotoActions } from '@/services/assets-api/AssetsPhotoService';
  import { user } from '@/ui/auth/auth';
  import { INSPECTION_STATUS, ISSUE_PERMISSIONS } from '@/_legacy/constants';
  import { useIssues } from '@/ui/concerns/conductInspection/useIssues';
  import { useQuestionOptions } from '@/ui/concerns/conductInspection/useQuestionOptions';

  interface FileProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any;
  }

  export default defineComponent<FileProps>({
    components: {
      CommentButton,
      RaiseIssueModal,
      PhotoButton,
      ActionButton,
      Loader,
      Icon,
      AttachedFile,
      ConfirmationModal,
    },
    props: ['item'],
    setup(props, ctx) {
      const loading = ref(false);
      const visibleFiles = ref<AssetsDocumentStub[]>([]);
      const modalDeleteOpened = ref(false);
      const modalIssueOpened = ref(false);
      const deleteIndex = ref<number | null>(null);

      const allowedMimeTypes = [
        ...getMimeTypes(['image', 'video', 'document']),
        ...extensionsWithNoMimeType(),
      ].join(',');

      const { findIssueByQuestionUuid, findIssueByKey } = useIssues();

      const addFile = (event: Event) => {
        const input = event.target as HTMLInputElement;

        if (!input.files) {
          return;
        }

        if ([...input.files].some(file => !isFileSizeValid(file))) {
          toasted.error(
            t('inspection-conduct.question.file.upload-file-failed',
              { maxAllowedSizeMb: '100' }), { duration: 5000 },
          );

          return;
        }

        if (!props.item.response) {
          Vue.set(props.item, 'response', []);
        }

        loading.value = true;

        for (const file of input.files) {
          try {
            const fileStub = getDocumentStub();
            setFileToAdd(fileStub, file);
            props.item.response.push(fileStub);
          } catch (error) {
            console.error(error);
          }
        }

        loading.value = false;
      };

      const deleteFile = (index: number) => {
        deleteIndex.value = index;
        modalDeleteOpened.value = true;
      };

      const closeDeleteModal = () => {
        deleteIndex.value = null;
        modalDeleteOpened.value = false;
      };

      const declineDeleteModal = () => {
        closeDeleteModal();
      };

      const confirmDeleteModal = () => {
        if (props.item.response && deleteIndex.value !== null && props.item.response[deleteIndex.value]) {
          if (!props.item.response[deleteIndex.value].file_id) {
            props.item.response.splice(deleteIndex.value, 1);
            closeDeleteModal();

            return;
          }

          const fileToDelete = props.item.response[deleteIndex.value];
          setFileToDelete(fileToDelete);
          props.item.response.splice(deleteIndex.value, 1, fileToDelete);
          Vue.set(props.item, 'response', [...props.item.response]);
        }

        closeDeleteModal();
      };

      const instance = getCurrentInstance();
      const showNotes = () => instance?.proxy?.$store?.commit('conduct/showNotesModal', props.item);
      const showPhotos = () => instance?.proxy?.$store?.commit('conduct/showPhotosModal', props.item);

      const containsPhotos = computed(() => props
        .item
        .photos
        ?.filter((photo: { action: string }) => photo.action !== AssetsPhotoActions.DELETE_IMAGE)
        .length > 0);

      const isIssuesAllowed = computed(() => props.item.issues_allowed !== false
        && (user.value.admin || user.value.permissions.includes(ISSUE_PERMISSIONS.CREATE_PERMISSION_NAME)));

      const issues = computed(() => getCurrentInstance()?.proxy?.$store.state.conduct.inspection.issues || {});
      const issuesArray = computed(() => Array.isArray(issues) ? issues : Object.values(issues));
      const raisedIssues = computed(() => findIssueByQuestionUuid((issuesArray.value || []), props.item.uuid));
      const newIssues = computed(() => findIssueByKey(issues.value, props.item.uuid));

      const containsIssues = computed(() => {
        return raisedIssues.value || newIssues.value;
      });

      const issuesDisabled = computed(() => {
        if (!instance?.proxy) {
          return true;
        }

        const { inspection_id, status } = instance.proxy.$store.state.conduct.inspection;

        return inspection_id && status === INSPECTION_STATUS.DONE && raisedIssues.value;
      });

      const openIssueModal = () => {
        modalIssueOpened.value = true;
      };

      const closeIssueModal = () => {
        modalIssueOpened.value = false;
      };

      const isFileVisible = (file: AssetsDocumentStub) => file.action !== AssetsDocumentActions.DELETE_DOCUMENT;

      watch(() => props.item.response, (response: AssetsDocumentStub[]) => {
        visibleFiles.value = (response || []).filter(isFileVisible);
        Vue.set(props.item, 'responded', visibleFiles.value.length > 0);
      }, { deep: true, immediate: true });

      return {
        loading,
        visibleFiles,
        modalDeleteOpened,
        modalIssueOpened,
        allowedMimeTypes,
        formatBytes,
        addFile,
        deleteFile,
        isFileVisible,
        closeDeleteModal,
        declineDeleteModal,
        confirmDeleteModal,
        showNotes,
        showPhotos,
        containsPhotos,
        isIssuesAllowed,
        issuesDisabled,
        containsIssues,
        openIssueModal,
        closeIssueModal,
      };
    },
  });
